import { Link } from 'react-router-dom';
import VerticalComparisonMatrix from '../../components/VerticalComparisonMatrix';

function Section({ title, bullets }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-[var(--gradient-card)] p-7 backdrop-blur-xl">
      <div className="text-lg font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
        {title}
      </div>
      <ul className="mt-4 grid gap-2 text-sm text-[var(--text-secondary)]">
        {bullets.map((b) => (
          <li key={b} className="leading-6">
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function BFSI() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[var(--gradient-hero)] p-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_30%,rgba(37,99,235,0.18),transparent_45%),radial-gradient(circle_at_70%_25%,rgba(6,182,212,0.10),transparent_55%)]" />
        <div className="relative">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Industries</div>
          <h1
            className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            BFSI (Banking, Financial Services & Insurance)
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
            Financial institutions require the highest levels of identity assurance, fraud prevention, and regulatory
            compliance. Identity management directly impacts Customer trust, transaction security, and regulatory
            standing.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-xl bg-[var(--accent-trust)] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20"
            >
              Request BFSI Assessment
            </Link>
            <Link
              to="/capabilities"
              className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.02] px-5 py-3 text-sm font-semibold text-[var(--text-primary)] hover:bg-white/[0.05]"
            >
              Explore Core Capabilities
            </Link>
          </div>
        </div>
      </div>

      <div className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          Customer Identity & Onboarding
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="Video KYC & eKYC"
            bullets={[
              "Real-time video-based Customer verification with liveness detection, document validation, and Aadhaar/PAN integration for instant account opening",
            ]}
          />
          <Section
            title="Progressive Customer Profiling"
            bullets={[
              "Risk-based identity collection allowing basic services with minimal KYC and enhanced verification for higher-value products",
            ]}
          />
          <Section
            title="Multi-Channel Identity Consistency"
            bullets={[
              "Unified Customer identity across mobile banking, internet banking, ATM, branch, and call center channels",
            ]}
          />
          <Section
            title="Beneficial Ownership Verification"
            bullets={[
              "Ultimate beneficial owner identification for corporate accounts meeting RBI and SEBI requirements",
            ]}
          />
          <Section
            title="Cross-Border Customer Identity"
            bullets={[
              "NRI/NRO Customer identity management with overseas document verification and correspondent banking compliance",
            ]}
          />
        </div>
      </div>

      <div className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          Transaction Security & Fraud Prevention
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="Adaptive Transaction Authentication"
            bullets={[
              "Risk-based step-up authentication for transactions based on amount, beneficiary, device, location, and behavioral signals",
            ]}
          />
          <Section
            title="Device Binding & Trust"
            bullets={[
              "Strong device identity linking for mobile banking with jailbreak/root detection and device health verification",
            ]}
          />
          <Section
            title="Biometric Transaction Authorization"
            bullets={[
              "Fingerprint, face, and voice biometrics for high-value transaction approval and account changes",
            ]}
          />
          <Section
            title="Real-Time Fraud Scoring"
            bullets={["ML-based identity risk scoring integrated into transaction flow for instant fraud detection"]}
          />
          <Section
            title="Mule Account Detection"
            bullets={["Identity pattern analysis to identify accounts used for money laundering and fund layering"]}
          />
          <Section
            title="SIM Swap Protection"
            bullets={[
              "Telecom integration to detect SIM swaps before OTP-based transactions, preventing account takeover",
            ]}
          />
        </div>
      </div>

      <div className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          Employee & Partner Identity
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="Branch Staff Identity Management"
            bullets={[
              "Role-based access for tellers, managers, and specialists with transaction limits and approval hierarchies",
            ]}
          />
          <Section
            title="Maker-Checker Enforcement"
            bullets={[
              "Identity-enforced dual control for critical operations including bulk transfers, rate changes, and limit modifications",
            ]}
          />
          <Section
            title="Agent/DSA Identity"
            bullets={[
              "Direct selling agent and business correspondent identity management with territory and product restrictions",
            ]}
          />
          <Section
            title="Vendor API Identity"
            bullets={[
              "OAuth/mTLS-based identity for fintech Partners, payment aggregators, and API banking consumers",
            ]}
          />
          <Section
            title="Trading Desk Controls"
            bullets={[
              "Real-time position monitoring and trade authorization linked to trader identity and desk limits",
            ]}
          />
        </div>
      </div>

      <div className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          Privileged & Sensitive Access
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="Core Banking PAM"
            bullets={[
              "Privileged access management for CBS with session isolation, command filtering, and complete audit trails",
            ]}
          />
          <Section
            title="Payment Switch Access"
            bullets={[
              "Just-in-time access for NPCI, SWIFT, and card network systems with multi-party approval",
            ]}
          />
          <Section
            title="Database Privileged Access"
            bullets={[
              "DBA access controls for Customer databases with data masking, query logging, and anomaly detection",
            ]}
          />
          <Section
            title="HSM & Key Management Access"
            bullets={[
              "Multi-custodian controls for hardware security module access and cryptographic key ceremonies",
            ]}
          />
        </div>
      </div>

      <div className="mt-14 rounded-3xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-xl sm:p-10">
        <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          Regulatory Compliance
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="RBI Cybersecurity Framework"
            bullets={[
              "Identity controls meeting Reserve Bank of India's cybersecurity framework requirements for banks",
            ]}
          />
          <Section
            title="SEBI CSCRF Compliance"
            bullets={[
              "Identity and access management aligned with Securities and Exchange Board of India's cyber security framework",
            ]}
          />
          <Section
            title="IRDAI Guidelines"
            bullets={[
              "Insurance sector identity controls meeting IRDAI's IT governance and security requirements",
            ]}
          />
          <Section
            title="PCI-DSS Compliance"
            bullets={[
              "Cardholder data access controls with strong authentication and access logging for PCI compliance",
            ]}
          />
          <Section
            title="SOX & Internal Audit"
            bullets={[
              "Segregation of duties enforcement and access certification for Sarbanes-Oxley compliance",
            ]}
          />
          <Section
            title="AML/CFT Identity Controls"
            bullets={[
              "Customer due diligence and enhanced verification for anti-money laundering compliance",
            ]}
          />
        </div>
      </div>

      <VerticalComparisonMatrix />
    </div>
  );
}
